/**
 * The ticket page's tabs — Step 45.
 *
 * Kai: *"there's still not multiple pages on the ticket page … for
 * associations, activities moved to its own page, resolution notes"*.
 *
 * **Each tab is a URL SEGMENT, not `useState`** — `/tickets/:id/activity` —
 * so a colleague can be sent straight to the tab that matters. That is the
 * standard `audit-ui[url-state]` enforces, and it is the difference between
 * "look at the ticket and click Activity" and a link that lands where you
 * meant.
 *
 * The properties rail stays put across all of them: it is the ticket's
 * identity, not one tab's content, and re-rendering it per tab would make
 * status and assignee unreachable from three of the four.
 */
import { NavLink } from 'react-router-dom';
import { ticketPath } from './api';
import { cn } from '../../lib/utils';

/**
 * The tab set, named from Kai's own owner table (2026-09-08).
 *
 * **"Associations" is deliberately NOT used here.** That name belongs to
 * Steps 48/49 — the TYPED Associate menu (Problem / Change) — and this tab
 * holds parent/child plus Step 21's untyped links, which the table calls
 * "Related tickets". Taking the other step's name would have meant renaming
 * this tab later, or worse, two tabs whose names imply the wrong split.
 *
 * Still owned elsewhere and deliberately absent until its step lands, since
 * an empty tab is worse than no tab: Approvals-on-KB (Step 33). Assets is out
 * of scope entirely — those live in Snipe-IT.
 *
 * **Problem** shows only on a problem and **Plan** only on a change: those
 * sections mean nothing on an incident, and the API refuses to write them
 * there. ("Plan" rather than "Change" — a tab called Change on a change reads
 * as a verb.)
 */
export const TICKET_TABS = [
  { slug: '', label: 'Details' },
  /**
   * ONE tab for everything a ticket is connected to.
   *
   * There were two — Related (parent/child/links) and Associations (the typed
   * problem/change relations) — and Kai, looking at a problem: *"on
   * both?!?!?!"*. Both listed connected tickets, so the split read as the same
   * thing said twice however carefully each half was worded. The distinction
   * was real in the DATA and invisible in the UI, which is the wrong place for
   * it to live: the sections inside carry it now, and the tab bar does not.
   *
   * Also just 'Related' rather than 'Related tickets', which wrapped onto two
   * lines and made one tab twice the height of its neighbours.
   */
  { slug: 'related', label: 'Related' },
  { slug: 'problem', label: 'Problem' },
  { slug: 'change', label: 'Plan' },
  /**
   * Step 54 — the outage record, and the review written about it.
   *
   * Two tabs, not one, for the same reason they are two tables: `Outage` is
   * written DURING, in a hurry; `Review` is written days later by somebody
   * else and gets published. Kai: *"a PIR should just be part of the MI
   * ticket"* — it is, as a tab on the ticket; the separate storage is
   * invisible from here.
   */
  { slug: 'outage', label: 'Outage' },
  { slug: 'review', label: 'Review' },
  { slug: 'tasks', label: 'Tasks' },
  { slug: 'approvals', label: 'Approvals' },
  { slug: 'activity', label: 'Activity' },
  { slug: 'resolution', label: 'Resolution' },
] as const;

/**
 * A TASK gets two tabs, and that is the point.
 *
 * Kai: *"tasks shouldn't need resolution notes, sub tasks, linked tickets
 * etc, it should only be able to have a parent ticket, nothing else"*, and
 * *"tasks should be so easy to close too, no requirements."*
 *
 * So no Resolution (nothing to write up — it is done or it is not), no Tasks
 * (they do not nest; a tree of tasks is a project, which this deliberately is
 * not), and no Related tickets (a task's only relationship is its parent, and
 * that is shown in the properties rail where it takes one line instead of a
 * tab). Activity stays because history is never noise.
 *
 * The API enforces every one of these independently — a hidden tab stops a
 * person, not an automation.
 */
export function tabsForType(type: string): readonly { slug: string; label: string }[] {
  // A task gets two. Everything else gets the set minus the tabs that only
  // make sense for one type.
  if (type === 'task') return TICKET_TABS.filter((t) => t.slug === '' || t.slug === 'activity');
  return TICKET_TABS.filter(
    (t) =>
      (t.slug !== 'problem' || type === 'problem') &&
      (t.slug !== 'change' || type === 'change') &&
      (t.slug !== 'outage' || type === 'major_incident') &&
      (t.slug !== 'review' || type === 'major_incident') &&
      // A change carries no resolution. Kai: *"a change doesn't need
      // resolution notes"* — it is asked a narrower question instead ("did it
      // work?"), which is the review outcome on the Plan tab.
      (t.slug !== 'resolution' || type !== 'change'),
  );
}

export function TicketTabs({
  id,
  type,
  counts,
}: {
  id: string;
  type: string;
  counts?: Record<string, number | undefined>;
}) {
  return (
    <nav
      className="flex shrink-0 items-center gap-1 overflow-x-auto border-b px-4"
      aria-label="Ticket sections"
    >
      {tabsForType(type).map((t) => (
        <NavLink
          key={t.slug}
          to={ticketPath(type, id, t.slug || undefined)}
          // `end` only on Conversation: without it the default tab would stay
          // marked active on every child route, which is the classic NavLink
          // trap and reads as two tabs being open at once.
          end={!t.slug}
          className={({ isActive }) =>
            cn(
              // `whitespace-nowrap` so no label can ever wrap and make one tab
              // taller than the rest — the bar scrolls sideways instead.
              'flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'border-brand text-brand'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )
          }
        >
          {t.label}
          {counts?.[t.slug] ? (
            <span className="rounded-full bg-muted px-1.5 text-[11px] tabular-nums">{counts[t.slug]}</span>
          ) : null}
        </NavLink>
      ))}
    </nav>
  );
}
